import type { GpuDto } from '@studio/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Cpu, Loader2, Play, Square } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/controls';
import { api, errorMessage } from '@/lib/api';
import { useGpu } from '@/lib/queries';
import { cn, duration, money } from '@/lib/utils';

const LABEL: Record<GpuDto['status'], string> = {
  off: 'Off',
  renting: 'Renting a GPU…',
  booting: 'Starting FreeVideo…',
  ready: 'Ready',
  stopping: 'Stopping…',
};

function useNow(everyMs = 1000) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), everyMs);
    return () => clearInterval(t);
  }, [everyMs]);
  return now;
}

/** GPU status: off → renting → booting → ready → stopping, with cost and the queue. */
export function GpuCard({ compact = false }: { compact?: boolean }) {
  const qc = useQueryClient();
  const { data: g } = useGpu();
  const now = useNow();
  const [confirmStop, setConfirmStop] = useState(false);
  const start = useMutation({
    mutationFn: () => api<GpuDto>('/api/gpu/start', { method: 'POST' }),
    onSuccess: (d) => qc.setQueryData(['gpu'], d),
    onError: (e) => toast.error(errorMessage(e)),
  });
  const stop = useMutation({
    mutationFn: () => api<GpuDto>('/api/gpu/stop', { method: 'POST' }),
    onSuccess: (d) => {
      qc.setQueryData(['gpu'], d);
      setConfirmStop(false);
      void qc.invalidateQueries({ queryKey: ['project'] });
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  if (!g) return <div className="h-20 animate-pulse rounded-lg bg-muted" />;
  const on = g.status !== 'off';
  const busy = g.status === 'renting' || g.status === 'booting' || g.status === 'stopping';
  const tone = g.status === 'ready' ? 'success' : busy ? 'warning' : 'secondary';
  const upFor = g.startedAt && on ? now - Date.parse(g.startedAt) : 0;
  const costNow = g.costPerHr != null && on && g.startedAt ? (g.costPerHr * upFor) / 3_600_000 : g.costSoFar;
  const work = g.queue.queued + g.queue.running + g.queue.waiting;
  const idleIn = g.idleStopAt ? Date.parse(g.idleStopAt) - now : null;

  return (
    <div className={cn('rounded-lg border bg-card p-3 text-sm', !compact && 'p-4')}>
      <div className="flex items-center gap-2">
        <Cpu className="size-4 text-muted-foreground" />
        <span className="font-medium">GPU</span>
        <Badge variant={tone} className="ml-auto">
          {busy && <Loader2 className="animate-spin" />}
          {LABEL[g.status]}
        </Badge>
      </div>
      {on && (
        <div className="mt-2 space-y-0.5 text-xs text-muted-foreground">
          <div className="truncate" title={g.gpuType ?? ''}>
            {g.gpuType ?? '—'} · {g.dataCenter}
          </div>
          <div className="tabular">
            {money(g.costPerHr)}/h · on {duration(upFor)} · {money(costNow, 3)} so far
          </div>
          {g.note && <div>{g.note}</div>}
          {idleIn != null && idleIn > 0 && <div>Stops when idle in {duration(idleIn)}</div>}
        </div>
      )}
      {work > 0 && (
        <div className="mt-1 text-xs text-muted-foreground">
          {g.queue.running ? `${g.queue.running} making · ` : ''}
          {g.queue.queued} queued{g.queue.waiting ? ` · ${g.queue.waiting} waiting for other shots` : ''}
        </div>
      )}
      {g.error && <div className="mt-2 rounded-md bg-destructive/5 p-2 text-xs text-destructive">{g.error}</div>}
      {!compact && (
        <div className="mt-3 grid gap-1 text-xs text-muted-foreground sm:grid-cols-2">
          <div>
            RunPod balance: <span className="tabular text-foreground">{money(g.balance)}</span> (minimum to start{' '}
            {money(g.minBalance)})
          </div>
          <div>Stops after {g.idleMinutes} minutes with nothing to make.</div>
          <div>Mode: {g.mode === 'fake' ? 'fake (simulated GPU, sample videos)' : 'real (RunPod)'}</div>
          {!g.configured && <div className="text-destructive">RunPod key not set on the server.</div>}
        </div>
      )}
      <div className="mt-3 flex gap-2">
        {g.status === 'off' && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => start.mutate()}
            loading={start.isPending}
            disabled={!g.configured}
          >
            <Play /> Start
          </Button>
        )}
        {on && g.status !== 'stopping' && (
          <Button size="sm" variant="outline" onClick={() => setConfirmStop(true)}>
            <Square /> Stop
          </Button>
        )}
      </div>
      <ConfirmDialog
        open={confirmStop}
        onOpenChange={setConfirmStop}
        title="Stop the GPU now?"
        description={
          work
            ? `${work} shot(s) waiting or being made will be cancelled.`
            : 'It is given back to RunPod and stops costing money.'
        }
        confirmLabel="Stop GPU"
        destructive
        loading={stop.isPending}
        onConfirm={() => stop.mutate()}
      />
    </div>
  );
}
